import { Instagram, Linkedin, Mail } from "lucide-react"
import { cn } from "@/lib/class-control"
import { PersonProfile } from "@/types/contract"

interface PersonProfileCardProps {
  person: PersonProfile
  className?: string
}

export function PersonProfileCard({ person, className }: PersonProfileCardProps) {
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div
      className={cn(
        "w-[384px] h-[729px]",
        "bg-[#15203c] rounded-[10px]",
        "p-[30px]",
        "flex flex-col gap-[28px]",
        className
      )}
    >
      {/* Header Section */}
      <div className="flex flex-col items-center gap-[16px]">
        <div className="w-[81.5px] h-[81.5px] rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
          {person.avatar ? (
            <img
              src={person.avatar}
              alt={person.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-[24px] font-medium text-white">
              {getInitials(person.name)}
            </span>
          )}
        </div>
        <div className="flex flex-col items-center gap-[4px]">
          <h2 className="text-[20px] font-semibold text-[#f6f6f6]">
            {person.name}
          </h2>
          <p className="text-[14px] font-normal text-[#b0b0b0]">
            {person.role}
          </p>
        </div>
      </div>

      {/* Info Section */}
      <div className="flex flex-col gap-[8px]">
        <div className="flex justify-between items-center">
          <span className="text-[12px] font-semibold text-white">Função:</span>
          <span className="text-[12px] font-normal text-white text-right">
            {person.role}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[12px] font-semibold text-white">Empresa:</span>
          <span className="text-[12px] font-normal text-white text-right">
            {person.company}
          </span>
        </div>
      </div>

      {/* Biography Section */}
      <div className="flex flex-col gap-[16px]">
        <h3 className="text-[14px] font-bold text-[#f6f6f6]">Biografia</h3>
        <p className="text-[14px] font-normal text-[#b0b0b0] leading-relaxed">
          {person.biography}
        </p>
      </div>

      {/* Contact Section */}
      <div className="flex flex-col gap-[8px]">
        <div className="flex justify-between items-center">
          <span className="text-[12px] font-semibold text-white">Telefone:</span>
          <span className="text-[12px] font-normal text-white text-right">
            {person.phone}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[12px] font-semibold text-white">E-mail:</span>
          <span className="text-[12px] font-normal text-white text-right">
            {person.email}
          </span>
        </div>
        {/* <div className="flex justify-between items-center">
          <span className="text-[12px] font-semibold text-white">Local:</span>
          <span className="text-[12px] font-normal text-white text-right">
            {person.location}
          </span>
        </div> */}
      </div>

      {/* Social Media Section */}
      <div className="flex flex-col gap-[16px]">
        <h3 className="text-[14px] font-bold text-[#f6f6f6]">Redes Sociais</h3>
        <div className="flex items-center gap-[20px]">
          {person.socialMedia.instagram && (
            <a
              href={person.socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#b0b0b0] hover:text-white transition-colors"
            >
              <Instagram size={24} />
            </a>
          )}
          {person.socialMedia.linkedin && (
            <a
              href={person.socialMedia.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#b0b0b0] hover:text-white transition-colors"
            >
              <Linkedin size={24} />
            </a>
          )}
          {person.socialMedia.email && (
            <a
              href={`mailto:${person.socialMedia.email}`}
              className="text-[#b0b0b0] hover:text-white transition-colors"
            >
              <Mail size={24} />
            </a>
          )}
        </div>
      </div>

      {/* Skills Section */}
      <div className="flex flex-col gap-[16px]">
        <h3 className="text-[14px] font-bold text-[#f6f6f6]">Skills</h3>
        <p className="text-[14px] font-normal text-[#b0b0b0] leading-relaxed">
          {person.skills}
        </p>
      </div>
    </div>
  )
}
